/**
 * Shared skeleton loading utilities for table components
 * Used across Purchase, Return, and other inventory modules
 */

/**
 * Constant for checkbox column width used across skeleton loaders
 */
export const SKELETON_CHECKBOX_WIDTH = 40;

/**
 * Type for tab-based column configuration
 * Maps tab names to arrays of column widths
 */
export type TabColumnConfig = Record<string, (number | string)[]>;

/**
 * Factory function to create skeleton column width getters
 * Allows easy configuration of column widths per tab with optional checkbox support
 * 
 * @param config - Tab-specific column width configurations
 * @param defaultWidths - Default column widths when tab not found in config
 * @returns Function that returns column widths based on selected tab and checkbox state
 * 
 * @example
 * const getWidths = createSkeletonColumnWidths(
 *   { tab1: [100, 200], tab2: [150, 250, 300] },
 *   [100, 150]
 * );
 * const widths = getWidths('tab1', true); // Returns [40, 100, 200]
 */
export const createSkeletonColumnWidths = (
  config: TabColumnConfig,
  defaultWidths: (number | string)[]
) => (selectedTab: string, hasCheckbox: boolean): (number | string)[] => {
  const checkboxWidth = hasCheckbox ? [SKELETON_CHECKBOX_WIDTH] : [];
  const widths = config[selectedTab] ?? defaultWidths;
  return [...checkboxWidth, ...widths];
};
